import Cookies from 'js-cookie';

import { resolveUrl } from '@change-corgi/core/dom';
import type { EnvironmentUtils } from '@change-corgi/core/environment';
import type { ErrorReporter } from '@change-corgi/core/errorReporter/common';
import type { FakeHistory } from '@change-corgi/core/react/router';
import type { Utilities } from '@change-corgi/core/react/utilityContext';

import type { Session, SessionConfiguration } from 'src/shared/session';

import type { AmplitudeGuidesAndSurveys } from 'src/app/shared/utils/amplitudeGuidesAndSurveys';
import type { AmplitudeReplay } from 'src/app/shared/utils/amplitudeReplay';

import type { HydrationData } from '../types';

import { getSession } from './session';
import {
	createAmplitudeGuidesAndSurveys,
	createAmplitudeReplay,
	createEventTracker,
	createExperiments,
	createFacebook,
	createFcmUtils,
	createGoogleAnalytics,
	createGoogleAuth,
	createGqlClient,
	createHttpClient,
	createNavigationUtils,
	createOptimizely,
	createUserAgentUtils,
	decorateWindowForPerimeterX,
	initOptimizely,
} from './utilities';
import { createCsrfUtils } from './utilities/csrf';

type Options = Readonly<{
	hydrationData: HydrationData | undefined;
	history: FakeHistory;
	environment: EnvironmentUtils;
	errorReporter: ErrorReporter;
}>;

type Result = Readonly<{
	utilities: Omit<Utilities, 'i18n'>;
	amplitudeGuidesAndSurveys: AmplitudeGuidesAndSurveys;
	amplitudeReplay: AmplitudeReplay;
	session: Session;
	sessionConfiguration: SessionConfiguration;
	sessionError?: true;
}>;

type CoreUtilities = Readonly<{
	gql: ReturnType<typeof createGqlClient>;
	http: ReturnType<typeof createHttpClient>;
}>;

type SessionData = Readonly<{
	session: Session;
	sessionConfiguration: SessionConfiguration;
	sessionError?: true;
}>;

type AmplitudeUtilities = Readonly<{
	amplitudeGuidesAndSurveys: AmplitudeGuidesAndSurveys;
	amplitudeReplay: AmplitudeReplay;
}>;

function createCoreUtilities({ errorReporter, environment }: Options): CoreUtilities {
	return {
		gql: createGqlClient({ errorReporter, environment }),
		http: createHttpClient({ errorReporter }),
	};
}

function createAmplitudeUtilities({
	environment,
	session,
	sessionConfiguration,
}: Readonly<{
	environment: EnvironmentUtils;
	session: Session;
	sessionConfiguration: SessionConfiguration;
}>): AmplitudeUtilities {
	return {
		amplitudeGuidesAndSurveys: createAmplitudeGuidesAndSurveys({ environment, session }),
		amplitudeReplay: createAmplitudeReplay({
			config: sessionConfiguration.featureConfigs.amplitudeSessionReplayConfig,
			environment,
			session,
		}),
	};
}

type IntermediateUtilities = Readonly<{
	csrf: ReturnType<typeof createCsrfUtils>;
	googleAnalytics: ReturnType<typeof createGoogleAnalytics>;
	userAgent: ReturnType<typeof createUserAgentUtils>;
	experiments: ReturnType<typeof createExperiments>;
	fcm: ReturnType<typeof createFcmUtils>;
	facebook: ReturnType<typeof createFacebook>;
	optimizely: ReturnType<typeof createOptimizely>;
	navigation: ReturnType<typeof createNavigationUtils>;
	googleAuth: ReturnType<typeof createGoogleAuth>;
}>;

function createIntermediateUtilities({
	errorReporter,
	environment,
	history,
	hydrationData,
	gql,
	session,
	sessionConfiguration,
}: Options & CoreUtilities & SessionData): IntermediateUtilities {
	return {
		csrf: createCsrfUtils({ errorReporter, session }),
		googleAnalytics: createGoogleAnalytics({ errorReporter, session, environment, history }),
		userAgent: createUserAgentUtils(),
		experiments: createExperiments({ gql, session }),
		fcm: createFcmUtils({ errorReporter, gql, session }),
		facebook: createFacebook({ errorReporter, session, environment }),
		optimizely: createOptimizely({ environment, sessionConfiguration }),
		navigation: createNavigationUtils(hydrationData),
		googleAuth: createGoogleAuth({ environment, errorReporter, userAgent: createUserAgentUtils() }),
	};
}

function createTrackerUtility({
	csrf,
	environment,
	session,
	optimizely,
	experiments,
	userAgent,
	http,
	facebook,
	amplitudeGuidesAndSurveys,
	amplitudeReplay,
}: Readonly<{
	csrf: ReturnType<typeof createCsrfUtils>;
	environment: EnvironmentUtils;
	session: Session;
	optimizely: ReturnType<typeof createOptimizely>;
	experiments: ReturnType<typeof createExperiments>;
	userAgent: ReturnType<typeof createUserAgentUtils>;
	http: ReturnType<typeof createHttpClient>;
	facebook: ReturnType<typeof createFacebook>;
	amplitudeGuidesAndSurveys: AmplitudeGuidesAndSurveys;
	amplitudeReplay: AmplitudeReplay;
}>) {
	return createEventTracker({
		csrf,
		environment,
		session,
		optimizely,
		experiments,
		userAgent,
		http,
		facebook,
		amplitudeGuidesAndSurveys,
		amplitudeReplay,
	});
}

type AssembleUtilitiesParams = Readonly<{
	errorReporter: ErrorReporter;
	environment: EnvironmentUtils;
	gql: ReturnType<typeof createGqlClient>;
	http: ReturnType<typeof createHttpClient>;
	session: Session;
	intermediateUtilities: IntermediateUtilities;
	amplitudeGuidesAndSurveys: AmplitudeGuidesAndSurveys;
	amplitudeReplay: AmplitudeReplay;
}>;

function assembleUtilities({
	errorReporter,
	environment,
	gql,
	http,
	session,
	intermediateUtilities,
	amplitudeGuidesAndSurveys,
	amplitudeReplay,
}: AssembleUtilitiesParams): Omit<Utilities, 'i18n'> {
	const { csrf, googleAnalytics, userAgent, experiments, fcm, facebook, optimizely, navigation, googleAuth } =
		intermediateUtilities;

	const tracker = createTrackerUtility({
		csrf,
		environment,
		session,
		optimizely,
		experiments,
		userAgent,
		http,
		facebook,
		amplitudeGuidesAndSurveys,
		amplitudeReplay,
	});

	return {
		csrf,
		environment,
		errorReporter,
		experiments,
		facebook,
		optimizely: initOptimizely(optimizely, { tracker }),
		googleAnalytics,
		fcm,
		gql,
		http,
		tracker,
		navigation,
		userAgent,
		cookies: Cookies,
		resolveUrl,
		googleAuth,
	};
}

export async function getUtilitiesAndSession(options: Options): Promise<Result> {
	const { hydrationData, environment, errorReporter } = options;

	const { gql, http } = createCoreUtilities(options);
	const sessionData = await getSession({ gql, errorReporter, hydrationData });
	const { session, sessionConfiguration, sessionError } = sessionData;

	const amplitudeUtils = createAmplitudeUtilities({
		environment,
		session,
		sessionConfiguration,
	});

	const intermediateUtilities = createIntermediateUtilities({
		...options,
		...sessionData,
		gql,
		http,
	});

	const utilities = assembleUtilities({
		errorReporter,
		environment,
		gql,
		http,
		session,
		intermediateUtilities,
		...amplitudeUtils,
	});

	if (environment.getEnvironment() !== 'production') {
		decorateWindowForPerimeterX();
	}

	return {
		utilities,
		session,
		sessionConfiguration,
		sessionError,
		...amplitudeUtils,
	};
}
